import React, { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Button, Link, MenuItem, Stack, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import performRequest from "network/perform-request";
import { leagueCustomerFinancialEntryFeeOptionsRoute } from "network/api-routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionAccess } from "hooks/useGestionAccess";
import { GESTION_AREAS } from "utils/gestion-access";
import { formatFinancialAmount } from "components/pages/Gestion/financial/financial-helpers";

/**
 * Picks the tournament's entry fee from the Finance price list (K-08). No price can be typed here.
 * `onChange` must be stable (useCallback): the preselect effect depends on it.
 */
const EntryFeeTemplateSelect = ({ customerId, tournamentId = null, value, onChange, disabled = false }) => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const { can } = useGestionAccess({ customerId });
  const [state, setState] = useState({ loaded: false, enabled: false, currency: "", selectedId: null, options: [] });
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    if (!customerId || !user) return Promise.resolve();
    const params = tournamentId ? { tournament_id: tournamentId } : {};
    return performRequest("GET", leagueCustomerFinancialEntryFeeOptionsRoute(customerId), params, true, user)
      .then((response) => {
        const data = response?.data || {};
        setError(false);
        setState({
          loaded: true,
          enabled: Boolean(data.enabled),
          currency: data.currency_code || "",
          selectedId: data.selected_id ?? null,
          options: Array.isArray(data.options) ? data.options : [],
        });
      })
      .catch(() => {
        setError(true);
        setState((previous) => ({ ...previous, loaded: true }));
      });
  }, [customerId, tournamentId, user]);

  useEffect(() => {
    load();
  }, [load]);

  // Preselect the backend's choice when the form has none, or when the saved entry is gone.
  useEffect(() => {
    if (!state.loaded || !state.enabled) return;
    const ids = state.options.map((option) => option.id);
    const current = value === "" || value == null ? null : Number(value);
    if (current === null || !ids.includes(current)) {
      const next = state.selectedId ?? "";
      if (next !== (current ?? "")) onChange(next);
    }
  }, [onChange, state, value]);

  if (!state.loaded) return null;
  // A failed reload keeps the last known options usable; the warning sits next to the select.
  const errorAlert = error ? (
    <Alert
      severity="warning"
      data-testid="entry-fee-load-error"
      action={
        <Button color="inherit" size="small" data-testid="entry-fee-retry" onClick={load}>
          {intl.formatMessage({ id: "gestion.leagueWizard.entryFee.retry" })}
        </Button>
      }
    >
      {intl.formatMessage({ id: "gestion.leagueWizard.entryFee.loadError" })}
    </Alert>
  ) : null;
  if (error && !(state.enabled && state.options.length > 0)) return errorAlert;
  if (!state.enabled) return null;

  const canManage = can(GESTION_AREAS.FINANCE);
  const manageLink = canManage ? (
    <Link
      href={`${GESTION_ROUTES.financials(customerId)}?tab=prices`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="entry-fee-manage-link"
      variant="body2"
    >
      {intl.formatMessage({ id: "gestion.leagueWizard.entryFee.manageLink" })}
    </Link>
  ) : null;

  if (state.options.length === 0) {
    return (
      <Stack spacing={1}>
        <Alert severity="info">{intl.formatMessage({ id: "gestion.leagueWizard.entryFee.empty" })}</Alert>
        {manageLink}
      </Stack>
    );
  }

  const optionLabel = (option) =>
    `${
      option.role === "global"
        ? intl.formatMessage({ id: "gestion.leagueWizard.entryFee.globalName" }, { name: option.name })
        : option.name
    } · ${formatFinancialAmount(option.amount, state.currency)}`;

  return (
    <Stack spacing={1}>
      <TextField
        select
        fullWidth
        label={intl.formatMessage({ id: "gestion.leagueWizard.entryFee.label" })}
        value={state.options.some((option) => option.id === Number(value)) ? value : ""}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
        SelectProps={{ onOpen: load }}
        helperText={intl.formatMessage({ id: "gestion.leagueWizard.entryFee.helper" })}
        inputProps={{ "data-testid": "entry-fee-select" }}
      >
        {state.options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {optionLabel(option)}
          </MenuItem>
        ))}
      </TextField>
      {errorAlert}
      {manageLink}
    </Stack>
  );
};

export default EntryFeeTemplateSelect;
